import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { PRIVACY_LIMITS, REQUEST_CHANNELS, RIGHTS_RESPONSE_DAYS, RETENTION_SCHEDULE, type CatalogueKey, type Member, type PrivacyRequest, type RequestChannel } from '@mahallu/shared';
import { ApiError, REGISTER_KEYS, loadErrorKey, privacy, usePrivacyRequests } from '../api';
import { FormFooter, FormSection, Modal, SelectField, TextAreaField } from '../components/Form';
import { Badge, PageHeader } from '../components/Parts';
import { dateTime } from '../format';

const errorText = (e: unknown) => (e instanceof ApiError ? e.message : String(e));

const FIELD_LABEL: Record<string, CatalogueKey> = { phone: 'field.phone', city: 'field.city', country: 'field.countryOnly', region: 'field.regionAbroad', work: 'field.work' };
const STATUS_TONE = { RECEIVED: 'warn', COMPLETED: 'ok', REJECTED: 'neutral' } as const;

/** Records an erasure request for one person; opened from their row in the house drawer. */
export function ErasureRequestForm({ member, onClose }: { member: Member; onClose: () => void }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [via, setVia] = useState<RequestChannel | ''>('');
  const [note, setNote] = useState('');
  const save = useMutation({
    mutationFn: () => privacy.createRequest({ memberId: member.id, receivedVia: via as RequestChannel, note: note.trim() || null }),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['privacy', 'requests'] });
      onClose();
    },
  });
  return (
    <Modal
      title={`${t('requests.new')} · ${member.nameEn}`}
      onClose={onClose}
      footer={<FormFooter onCancel={onClose} saving={save.isPending} saveLabel={t('requests.record')} error={save.isError ? errorText(save.error) : undefined} />}
    >
      <form
        id="active-form"
        onSubmit={(e) => {
          e.preventDefault();
          save.mutate();
        }}
      >
        <p className="callout small">{t('requests.newHint', { days: RIGHTS_RESPONSE_DAYS })}</p>
        <FormSection title={t('requests.section')}>
          <SelectField<RequestChannel> label={t('requests.via')} value={via} onChange={setVia} required empty={t('form.choose')} options={REQUEST_CHANNELS.map((v) => ({ value: v, label: t(`via.${v}`) }))} />
          <TextAreaField label={t('requests.note')} value={note} onChange={setNote} maxLength={PRIVACY_LIMITS.note} rows={3} full />
        </FormSection>
      </form>
    </Modal>
  );
}

function DecideForm({ request, onClose }: { request: PrivacyRequest; onClose: () => void }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [decision, setDecision] = useState<'complete' | 'reject' | ''>('');
  const [note, setNote] = useState('');
  const save = useMutation({
    mutationFn: () =>
      decision === 'complete' ? privacy.completeRequest(request.id, request.version, note.trim() || null) : privacy.rejectRequest(request.id, request.version, note.trim()),
    onSuccess: async () => {
      // Erasure changes the person's row in the register too.
      await Promise.all([qc.invalidateQueries({ queryKey: ['privacy', 'requests'] }), ...REGISTER_KEYS.map((queryKey) => qc.invalidateQueries({ queryKey }))]);
      onClose();
    },
  });
  return (
    <Modal
      title={`${t('requests.decide')} · ${request.requestNo}`}
      onClose={onClose}
      footer={<FormFooter onCancel={onClose} saving={save.isPending} saveLabel={decision === 'reject' ? t('requests.reject') : t('requests.complete')} error={save.isError ? errorText(save.error) : undefined} />}
    >
      <form
        id="active-form"
        onSubmit={(e) => {
          e.preventDefault();
          save.mutate();
        }}
      >
        <dl className="kv">
          <div>
            <dt>{t('requests.person')}</dt>
            <dd>
              {request.member.nameEn} · {request.member.houseNo}
            </dd>
          </div>
          <div>
            <dt>{t('requests.note')}</dt>
            <dd>{request.requestNote ?? t('common.notGiven')}</dd>
          </div>
        </dl>
        <FormSection title={t('requests.decide')}>
          <SelectField
            label={t('requests.decision')}
            value={decision}
            onChange={setDecision}
            required
            empty={t('form.choose')}
            full
            hint={decision === 'complete' ? t('requests.completeHint') : undefined}
            options={[
              { value: 'complete', label: t('requests.complete') },
              { value: 'reject', label: t('requests.reject') },
            ]}
          />
          <TextAreaField label={decision === 'reject' ? t('requests.reason') : t('requests.decisionNote')} value={note} onChange={setNote} maxLength={PRIVACY_LIMITS.note} rows={3} required={decision === 'reject'} full />
        </FormSection>
      </form>
    </Modal>
  );
}

function Outcome({ r }: { r: PrivacyRequest }) {
  const { t } = useTranslation();
  if (r.status === 'REJECTED') return <span className="note">{r.decisionNote}</span>;
  if (!r.outcome) return null;
  const erased = r.outcome.erased.map((f) => t(FIELD_LABEL[f] ?? 'field.phone')).join(', ');
  const kept = r.outcome.kept.map((k) => RETENTION_SCHEDULE.find((x) => x.record === k.record)?.labelEn ?? k.record);
  return (
    <span className="note" title={kept.join(' · ')}>
      {erased ? t('requests.erasedFields', { list: erased }) : t('requests.nothingToErase')}
    </span>
  );
}

/** Register › Privacy requests (pilot slice 5): erasure requests and their answers. */
export function PrivacyRequests() {
  const { t, i18n } = useTranslation();
  const [status, setStatus] = useState<'open' | 'closed' | 'all'>('open');
  const list = usePrivacyRequests(status);
  const [deciding, setDeciding] = useState<PrivacyRequest | null>(null);
  return (
    <>
      <PageHeader icon="shield" title={t('requests.title')} desc={t('requests.desc')} />
      <div className="action-row filter-row">
        <SelectField
          label={t('requests.show')}
          value={status}
          onChange={(v) => setStatus(v || 'open')}
          options={(['open', 'closed', 'all'] as const).map((v) => ({ value: v, label: t(`requests.filter.${v}`) }))}
        />
        <span className="spacer" />
        <p className="note">{t('requests.fromHouse')}</p>
      </div>
      {list.isError ? <p className="form-error">{t(loadErrorKey(list.error))}</p> : null}
      <div className="tbl-wrap">
        <div className="tbl-scroll">
          <table>
            <thead>
              <tr>
                <th>{t('requests.no')}</th>
                <th>{t('requests.person')}</th>
                <th>{t('requests.received')}</th>
                <th>{t('requests.due')}</th>
                <th>{t('requests.status')}</th>
                <th>{t('requests.outcome')}</th>
                <th>{t('col.actions')}</th>
              </tr>
            </thead>
            <tbody>
              {list.isPending ? (
                <tr>
                  <td colSpan={7} className="note">
                    {t('requests.loading')}
                  </td>
                </tr>
              ) : list.data?.length ? (
                list.data.map((r) => (
                  <tr key={r.id}>
                    <td className="num">{r.requestNo}</td>
                    <td>
                      <b>{r.member.nameEn}</b>
                      {r.member.nameMl ? <span className="ml note"> {r.member.nameMl}</span> : null}
                      <div className="note num">
                        {r.member.houseNo} · {r.member.houseName}
                      </div>
                    </td>
                    <td>
                      {dateTime(r.createdAt, i18n.language)}
                      <div className="note">{t(`via.${r.receivedVia}`)}</div>
                    </td>
                    <td className="num">
                      {r.dueOn} {r.overdue ? <Badge tone="bad">{t('requests.overdue')}</Badge> : null}
                    </td>
                    <td>
                      <Badge tone={STATUS_TONE[r.status]}>{t(`rstatus.${r.status}`)}</Badge>
                    </td>
                    <td>
                      <Outcome r={r} />
                    </td>
                    <td>
                      {r.status === 'RECEIVED' ? (
                        <button type="button" className="btn sm" onClick={() => setDeciding(r)}>
                          {t('requests.decide')}
                        </button>
                      ) : null}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={7} className="note">
                    {t('requests.none')}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
      {deciding ? <DecideForm request={deciding} onClose={() => setDeciding(null)} /> : null}
    </>
  );
}
